<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'exercises' }">
          Exercise</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-1 font-weight-bold my-5 text-capitalize">
        Add Exercise
      </div>

      <div class="col-xl-4 col-lg-6 col-md-12 mt-6 pa-0">
        <v-form @submit.prevent="submit" v-model="valid" ref="form">
          <v-divider class="pb-5"></v-divider>

          <label class="text-field-label">Name</label>
          <v-text-field
            flat
            solo
            required
            counter
            :rules="rules.name"
            class="mt-2"
            maxlength="255"
            v-model="form.name"
            placeholder="Name"
            :error-messages="form.$getError('name')"
          ></v-text-field>

          <label class="text-field-label">Description</label>
          <v-textarea
            solo
            maxlength="255"
            name="input-7-4"
            v-model="form.description"
            placeholder="Description"
            :error-messages="form.$getError('description')"
          ></v-textarea>

          <label class="text-field-label">Type</label>
          <v-select
            flat
            solo
            required
            class="mt-2"
            v-model="form.effort_type"
            placeholder="Type"
            :rules="rules.effort_type"
            :items="type_options"
            :error-messages="form.$getError('effort_type')"
          ></v-select>

          <label class="text-field-label">Exercise Categories</label>
          <v-select
            flat
            solo
            class="mt-2"
            aspect-ratio="1"
            :items="exercise_category_details"
            item-text="label"
            item-value="value"
            placeholder="Select Exercise Categories"
            v-model="form.exercise_category_ids"
            multiple
            chips
          ></v-select>

          <label class="text-field-label">Equipments</label>
          <v-select
            flat
            solo
            v-model="form.equipment_ids"
            :items="equipments_details"
            item-text="label"
            item-value="value"
            multiple
            chips
            placeholder="Select Equipments"
            persistent-hint
          ></v-select>

          <label class="text-field-label">Beginner Reps</label>
          <div class="d-flex mb-4">
            <v-text-field
              flat
              solo
              required
              counter
              :rules="rules.name"
              class="mt-2"
              maxlength="255"
              v-model="form.exercise_levels.beginner"
              placeholder="Beginner Reps"
              type="number"
            ></v-text-field>
          </div>

          <label class="text-field-label">Intermediate Reps</label>
          <div class="d-flex mb-4">
            <v-text-field
              flat
              solo
              required
              counter
              :rules="rules.name"
              class="mt-2"
              maxlength="255"
              v-model="form.exercise_levels.intermediate"
              placeholder="Intermediate Reps"
              type="number"
            ></v-text-field>
          </div>

          <label class="text-field-label">Advanced Reps</label>
          <div class="d-flex mb-4">
            <v-text-field
              flat
              solo
              required
              counter
              :rules="rules.name"
              class="mt-2"
              maxlength="255"
              v-model="form.exercise_levels.advanced"
              placeholder="Advanced Reps"
              type="number"
            ></v-text-field>
          </div>

          <label class="text-field-label">Right Intro Video</label>
          <v-autocomplete
            flat
            solo
            class="mt-2"
            aspect-ratio="1"
            :items="video_details"
            item-text="label"
            item-value="value"
            placeholder="Select Right Intro Video"
            v-model="form.videos.right_intro"
          ></v-autocomplete>

          <label class="text-field-label">Right Exercise Video</label>
          <v-autocomplete
            flat
            solo
            class="mt-2"
            aspect-ratio="1"
            :items="video_details"
            item-text="label"
            item-value="value"
            placeholder="Select Right Exercise Video"
            v-model="form.videos.right_exercise"
          ></v-autocomplete>

          <label class="text-field-label">Left Intro Video</label>
          <v-autocomplete
            flat
            solo
            class="mt-2"
            aspect-ratio="1"
            :items="video_details"
            item-text="label"
            item-value="value"
            placeholder="Select Left Intro Video"
            v-model="form.videos.left_intro"
          ></v-autocomplete>

          <label class="text-field-label">Left Exercise Video</label>
          <v-autocomplete
            flat
            solo
            class="mt-2"
            aspect-ratio="1"
            :items="video_details"
            item-text="label"
            item-value="value"
            placeholder="Select Left Exercise Video"
            v-model="form.videos.left_exercise"
          ></v-autocomplete>

          <label class="text-field-label">Thumbnail</label>
          <v-file-input
            :rules="rules.photo"
            accept="image/png, image/jpeg, image/bmp"
            placeholder="Pick a photo"
            :prepend-icon="icons.camera"
            v-model="form.photo"
            :error-messages="form.$getError('photo')"
          ></v-file-input>

          <v-divider class="my-5"></v-divider>
          <div class="mt-4">
            <v-btn type="submit" color="primary" :loading="form.$busy"
              >Submit</v-btn
            >
          </div>
        </v-form>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text @click="snackbar.show = false" v-bind="attrs">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Form from '@/utils/form'
import { mdiChevronLeft, mdiCamera } from '@mdi/js'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import { mapActions, mapState } from 'vuex'
import { each } from 'lodash'

export default {
  name: 'ExerciseCreate',

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      valid: false,
      photoCover: null,
      showUpload: false,
      type_options: ['reps', 'duration'],
      form: new Form({
        name: '',
        description: '',
        effort_type: null,
        exercise_category_ids: [],
        videos: {
          right_intro: null,
          right_exercise: null,
          left_intro: null,
          left_exercise: null,
        },
        exercise_levels: {
          beginner: null,
          intermediate: null,
          advanced: null,
        },
        photo: null,
        equipment_ids: [],
      }),
      equipments_details: [],
      exercise_category_details: [],
      video_details: [],
      icons: {
        arrowLeft: mdiChevronLeft,
        camera: mdiCamera,
      },
      rules: {
        name: [(v) => !!v || 'Name is required'],
        photo: [(v) => !!v || 'Photo is required'],
        effort_type: [(v) => !!v || 'Type is required'],
      },
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
    }
  },

  computed: {
    ...mapState({
      exerciseDetails: (state) => state.exercise.exerciseDetails,
      exerciseItemCategory: (state) => state.exercise.exerciseItemCategory,
      video: (state) => state.video.list,
      equipments: (state) => state.exercise.equipments,
    }),
  },

  created() {
    this.getExerciseItemsCategory()
    this.getVideoDetails()
    this.getEquipmentsDetails()
  },

  methods: {
    ...mapActions({
      storeExercise: 'exercise/storeExercise',
      getExerciseItemCategory: 'exercise/getExerciseItemCategory',
      getVideos: 'video/getVideos',
      getEquipments: 'exercise/getEquipments',
    }),

    async getExerciseItemsCategory() {
      this.loading = true
      await this.getExerciseItemCategory()
      each(this.exerciseItemCategory.exercise_category, (value, key) => {
        this.exercise_category_details.push({
          label: value.category_name,
          value: value.id,
        })
      })

      this.loading = false
    },

    async getEquipmentsDetails() {
      await this.getEquipments()
      each(this.equipments, (value, key) => {
        this.equipments_details.push({
          label: value.name,
          value: value.id,
        })
      })
    },

    async getVideoDetails(page = 1) {
      let params = {
        page,
      }

      await this.getVideos(params)
      each(this.video, (value, key) => {
        this.video_details.push({
          label: value.name,
          value: value.id,
        })
      })
    },

    async submit() {
      this.form.$busy = true
      this.form.$clearErrors()

      const formData = new FormData()
      each(this.form.$data(), (value, key) => {
        if (key === 'videos' || key === 'exercise_levels') {
          each(value, (v, k) => {
            if (v) formData.append(`${key}[${k}]`, v)
          })
        } else if (['equipment_ids', 'exercise_category_ids'].includes(key)) {
          each(value, (v, k) => {
            formData.append(`${key}[]`, v)
          })
        } else {
          formData.append(key, value)
        }
      })

      this.storeExercise(formData)
        .then(() => {
          this.showSnackbar('Exercises successfully created!', 'success')

          setTimeout(() => {
            this.$router.push({
              name: 'exercises',
            })
          }, 1000)
        })
        .catch((err) => {
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }

          this.form.$busy = false
        })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },
}
</script>
